import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  ShieldCheck, 
  DollarSign, 
  Sliders, 
  Copy, 
  Check, 
  ChevronRight, 
  AlertTriangle, 
  Award, 
  BarChart3, 
  Download, 
  Layers, 
  Zap, 
  CheckCircle2, 
  RefreshCw 
} from 'lucide-react';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { generateContentWithCache } from '../../../lib/geminiService';
import { parseAIJSON } from '../../../utils/aiUtils';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';

interface MarketIntelligenceRadarProps {
  userCity?: string;
  userState?: string;
  userNiche?: string;
  currentVhbRate: number;
  monthlyBillableHours?: number;
  onBack: () => void;
  onApplyNewVhbRate?: (newRate: number) => void;
}

interface BenchmarkData {
  economicMin: number;
  economicMax: number;
  averageMin: number;
  averageMax: number;
  premiumMin: number;
  premiumMax: number;
  marketInsights: string;
  antiObjectionScripts: {
    title: string;
    trigger: string;
    script: string;
  }[];
}

export default function MarketIntelligenceRadar({
  userCity = 'São Paulo',
  userState = 'SP',
  userNiche = 'Serviços Técnicos e Especializados',
  currentVhbRate = 85,
  monthlyBillableHours = 110,
  onBack,
  onApplyNewVhbRate
}: MarketIntelligenceRadarProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [appliedSuccess, setAppliedSuccess] = useState(false);
  
  // Slider interativo de reposicionamento
  const [simulatedVhb, setSimulatedVhb] = useState<number>(currentVhbRate);

  // Sincroniza o valor simulado quando o valor atual mudar
  useEffect(() => {
    setSimulatedVhb(currentVhbRate);
  }, [currentVhbRate]);

  // Dados com fallback inteligente baseado em cálculos de mercado
  const defaultBenchmark: BenchmarkData = useMemo(() => {
    const base = currentVhbRate > 20 ? currentVhbRate : 80;
    return {
      economicMin: Math.round(base * 0.55),
      economicMax: Math.round(base * 0.8),
      averageMin: Math.round(base * 0.85),
      averageMax: Math.round(base * 1.25),
      premiumMin: Math.round(base * 1.3),
      premiumMax: Math.round(base * 2.1),
      marketInsights: `Na região de ${userCity} - ${userState}, clientes de ${userNiche} valorizam pontualidade, garantia formal e nota fiscal, aceitando pagar até 45% acima da média por confiança.`,
      antiObjectionScripts: [
        {
          title: 'Quando o cliente diz que o concorrente faz por metade',
          trigger: 'Cliente diz que encontrou alguém mais barato',
          script: `Entendo perfeitamente que o preço seja um fator importante! A diferença é que nosso orçamento inclui materiais de 1ª linha com procedência, garantia formal de execução e comprovante de entrega do serviço. Na maioria das vezes, o "barato" exige refazer o serviço em 3 meses e acaba custando o dobro. Aqui você tem segurança e nota fiscal.`
        },
        {
          title: 'Como responder quando pedem desconto',
          trigger: 'Cliente pede desconto sem reduzir o escopo',
          script: `Nossos preços são calculados com base no tempo de trabalho exato e materiais necessários para garantir a máxima qualidade. Para reduzir o valor final, podemos ajustar o escopo ou parcelar as etapas, garantindo que você receba o melhor resultado sem abrir mão da segurança.`
        },
        {
          title: 'Como explicar por que o seu trabalho vale mais',
          trigger: 'Cliente questiona o valor da hora ou diária',
          script: `Nós investimos constantemente em ferramentas de precisão, normas de segurança e cumprimento rigoroso de prazos. Você não está pagando apenas pelas horas de execução, mas pelos anos de experiência que garantem que o trabalho seja feito certo logo na primeira vez.`
        }
      ]
    };
  }, [currentVhbRate, userCity, userState, userNiche]);

  const [benchmark, setBenchmark] = useState<BenchmarkData>(defaultBenchmark);

  // Busca de Inteligência com Gemini
  const fetchMarketIntelligence = async () => {
    setIsLoading(true);
    try {
      const prompt = `Você é o maior especialista em inteligência de mercado e precificação de serviços no Brasil para o setor de micro e pequenas empresas.
Cidade: ${userCity} - ${userState}
Nicho: ${userNiche}
Valor Hora Base Atual do Usuário: R$ ${currentVhbRate}

Gere um diagnóstico de inteligência competitiva com 3 faixas reais de valor hora cobradas no mercado local:
1. Faixa Econômica (concorrentes informais sem garantia)
2. Faixa Média de Mercado (prestadores regulares)
3. Faixa Premium / Alto Padrão (especialistas que oferecem garantia, contrato e laudo)
Gere também 3 roteiros de respostas rápidas para objeções frequentes de clientes no WhatsApp.

Retorne ESTRITAMENTE um JSON no seguinte formato:
{
  "economicMin": number,
  "economicMax": number,
  "averageMin": number,
  "averageMax": number,
  "premiumMin": number,
  "premiumMax": number,
  "marketInsights": "string curta de 1 ou 2 frases com visão executiva",
  "antiObjectionScripts": [
    {
      "title": "string",
      "trigger": "string",
      "script": "string"
    }
  ]
}`;

      const response = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents: [{ parts: [{ text: prompt }] }],
        config: { responseMimeType: 'application/json' }
      });

      const parsed = parseAIJSON(response.text || '', null);
      if (parsed && typeof parsed.averageMin === 'number') {
        setBenchmark({
          economicMin: parsed.economicMin || defaultBenchmark.economicMin,
          economicMax: parsed.economicMax || defaultBenchmark.economicMax,
          averageMin: parsed.averageMin || defaultBenchmark.averageMin,
          averageMax: parsed.averageMax || defaultBenchmark.averageMax,
          premiumMin: parsed.premiumMin || defaultBenchmark.premiumMin,
          premiumMax: parsed.premiumMax || defaultBenchmark.premiumMax,
          marketInsights: parsed.marketInsights || defaultBenchmark.marketInsights,
          antiObjectionScripts: Array.isArray(parsed.antiObjectionScripts) && parsed.antiObjectionScripts.length > 0
            ? parsed.antiObjectionScripts
            : defaultBenchmark.antiObjectionScripts
        });
      }
    } catch (e) {
      console.warn('Usando inteligência calculada local:', e);
      setBenchmark(defaultBenchmark);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketIntelligence();
  }, [userCity, userState, userNiche]);

  // Cálculos do simulador de ganho
  const diffPerHour = Math.max(0, simulatedVhb - currentVhbRate);
  const extraMonthlyRevenue = diffPerHour * monthlyBillableHours;
  const extraYearlyRevenue = extraMonthlyRevenue * 12;

  // Determinação da faixa onde o usuário está
  const userPositionTier = useMemo(() => {
    if (currentVhbRate <= benchmark.economicMax) return { label: 'Faixa Econômica', color: 'text-amber-600 bg-amber-50 border-amber-200' };
    if (currentVhbRate <= benchmark.averageMax) return { label: 'Média do Mercado Local', color: 'text-blue-600 bg-blue-50 border-blue-200' };
    return { label: 'Preço de Especialista', color: 'text-brand bg-emerald-50 border-emerald-200' };
  }, [currentVhbRate, benchmark]);

  const handleCopyScript = (scriptText: string, index: number) => {
    navigator.clipboard.writeText(scriptText);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2500);
  };

  const handleApplySimulatedRate = () => {
    if (onApplyNewVhbRate) {
      onApplyNewVhbRate(simulatedVhb);
      setAppliedSuccess(true);
      setTimeout(() => setAppliedSuccess(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">

      {/* Header Slim (56px) — Padrão R-MAX */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 h-14 flex items-center justify-between shadow-2xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
            title="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
              Preços do Mercado Local
            </h1>
            <p className="text-[11px] text-zinc-500 truncate font-medium">
              {userNiche} · {userCity} – {userState}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <ScreenTourButton screenKey="radar" />
          <button
            type="button"
            onClick={fetchMarketIntelligence}
            disabled={isLoading}
            className="p-2 sm:px-3 sm:py-1.5 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer border border-zinc-200/80 transition-colors active:scale-95 disabled:opacity-50 shrink-0"
            title="Atualizar dados"
          >
            <RefreshCw className={`h-4 w-4 text-brand ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* 1. Card Hero: Diagnóstico de Posicionamento */}
        <div className="bg-zinc-900 text-white rounded-3xl p-5 sm:p-6 shadow-sm space-y-5">

          {/* Posição atual */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1.5">
              <p className="text-[11px] font-black text-emerald-400 uppercase tracking-widest">
                📊 Sua hora em relação ao mercado
              </p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-zinc-400 text-sm font-semibold">Sua hora atual:</span>
                <span className="text-2xl sm:text-3xl font-black text-white">{formatCurrencyBRL(currentVhbRate)}<span className="text-base text-zinc-400 font-bold">/h</span></span>
              </div>
              <p className="text-xs text-zinc-500 font-medium">
                {userNiche} · {userCity} – {userState}
              </p>
            </div>

            <span className={`text-xs font-black uppercase px-3.5 py-1.5 rounded-2xl border self-start ${userPositionTier.color}`}>
              {userPositionTier.label}
            </span>
          </div>

          {/* Faixas do Mercado — Cards compactos */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-black text-zinc-400 uppercase tracking-wider block leading-tight">
                Econômico
              </span>
              <p className="text-xs sm:text-sm font-black text-zinc-200 leading-snug">
                {formatCurrencyBRL(benchmark.economicMin)}<br />
                <span className="text-zinc-400">–</span><br />
                {formatCurrencyBRL(benchmark.economicMax)}
              </p>
              <span className="text-[10px] text-zinc-500 font-medium">Sem garantia</span>
            </div>

            <div className="bg-blue-950/40 border border-blue-800/50 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-black text-blue-400 uppercase tracking-wider block leading-tight">
                Média Local
              </span>
              <p className="text-xs sm:text-sm font-black text-blue-200 leading-snug">
                {formatCurrencyBRL(benchmark.averageMin)}<br />
                <span className="text-blue-400">–</span><br />
                {formatCurrencyBRL(benchmark.averageMax)}
              </p>
              <span className="text-[10px] text-zinc-500 font-medium">Prestadores</span>
            </div>

            <div className="bg-emerald-950/40 border border-emerald-800/50 rounded-2xl p-3 space-y-1">
              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider block leading-tight">
                Especialista
              </span>
              <p className="text-xs sm:text-sm font-black text-emerald-200 leading-snug">
                {formatCurrencyBRL(benchmark.premiumMin)}<br />
                <span className="text-emerald-400">–</span><br />
                {formatCurrencyBRL(benchmark.premiumMax)}
              </p>
              <span className="text-[10px] text-emerald-500 font-medium">Com garantia</span>
            </div>
          </div>

          {/* Insight do Mercado */}
          {isLoading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-xs font-medium animate-pulse py-1">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              <span>Consultando inteligência de mercado...</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-zinc-400 text-xs font-medium leading-relaxed border-t border-white/10 pt-3">
              <Zap className="h-3.5 w-3.5 text-brand shrink-0 mt-0.5" />
              <span>{benchmark.marketInsights}</span>
            </div>
          )}
        </div>

        {/* 2. Simulador de Reajuste */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-zinc-200/80 shadow-2xs space-y-5">
          <div className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-2xl bg-emerald-50 flex items-center justify-center border border-emerald-100">
                <Sliders className="h-5 w-5 text-brand" />
              </div>
              <div>
                <h3 className="text-sm font-black text-zinc-900">
                  Simule o Reajuste da Sua Hora
                </h3>
                <p className="text-[11px] text-zinc-500 font-medium">
                  Veja quanto você ganha a mais por ano 💰
                </p>
              </div>
            </div>

            {appliedSuccess && (
              <span className="text-xs font-bold text-brand bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Atualizado!
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-bold text-zinc-600">Novo valor hora simulado:</span>
                <span className="text-2xl font-black text-brand">{formatCurrencyBRL(simulatedVhb)}<span className="text-sm text-zinc-400 font-bold">/h</span></span>
              </div>
              <input
                type="range"
                min={Math.round(benchmark.economicMin * 0.8)}
                max={Math.round(benchmark.premiumMax * 1.2)}
                step={5}
                value={simulatedVhb}
                onChange={e => setSimulatedVhb(Number(e.target.value))}
                className="w-full h-2.5 bg-zinc-100 rounded-lg appearance-none cursor-pointer accent-brand"
              />
              <div className="flex justify-between text-[10px] text-zinc-400 font-mono">
                <span>{formatCurrencyBRL(Math.round(benchmark.economicMin * 0.8))}</span>
                <span>Média: {formatCurrencyBRL(benchmark.averageMin)}</span>
                <span>Topo: {formatCurrencyBRL(Math.round(benchmark.premiumMax * 1.2))}</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100 space-y-1">
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Ganho extra no mês</span>
                <h4 className="text-lg font-black text-zinc-900">
                  {diffPerHour > 0 ? `+ ${formatCurrencyBRL(extraMonthlyRevenue)}/mês` : 'Sem alteração'}
                </h4>
                <p className="text-[10px] text-zinc-500 font-medium">Com {monthlyBillableHours}h produtivas/mês</p>
              </div>

              <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-100 space-y-1">
                <span className="text-[10px] text-brand font-black uppercase tracking-widest block">Ganho extra em 12 meses</span>
                <h4 className="text-xl font-black text-brand">
                  {diffPerHour > 0 ? `+ ${formatCurrencyBRL(extraYearlyRevenue)}/ano` : 'R$ 0,00'}
                </h4>
                <p className="text-[10px] text-emerald-800 font-medium">Sem trabalhar mais horas 🚀</p>
              </div>
            </div>

            {diffPerHour > 0 && onApplyNewVhbRate && (
              <button
                type="button"
                onClick={handleApplySimulatedRate}
                className="w-full py-3.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-sm shadow-brand/20 flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95"
              >
                <Zap className="h-4 w-4" />
                <span>Aplicar {formatCurrencyBRL(simulatedVhb)}/h como Minha Nova Hora</span>
              </button>
            )}
          </div>
        </div>

        {/* 3. Respostas Anti-Objeção para WhatsApp */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <h3 className="text-sm font-black text-zinc-900">
                💬 Respostas Prontas para o WhatsApp
              </h3>
              <p className="text-[11px] text-zinc-500 font-medium">Quando o cliente reclamar do preço, use estas respostas:</p>
            </div>
            <span className="text-[10px] font-bold text-zinc-400 bg-white border border-zinc-200 px-2 py-0.5 rounded-lg">Toque para copiar</span>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            {benchmark.antiObjectionScripts.map((item, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-5 border border-zinc-200/80 shadow-2xs flex flex-col justify-between space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-brand bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">
                      Situação {idx + 1}
                    </span>
                  </div>
                  <h4 className="text-xs font-black text-zinc-900 leading-snug">{item.title}</h4>
                  <p className="text-[10px] text-zinc-500 font-medium italic bg-zinc-50 px-2 py-1 rounded-lg">
                    "{item.trigger}"
                  </p>
                  <p className="text-xs text-zinc-700 font-medium leading-relaxed line-clamp-5">
                    {item.script}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleCopyScript(item.script, idx)}
                  className={`w-full py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all cursor-pointer active:scale-95 ${
                    copiedIndex === idx
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-800'
                  }`}
                >
                  {copiedIndex === idx ? (
                    <>
                      <Check className="h-3.5 w-3.5 stroke-3" />
                      <span>Copiado! Cola no Zap 📋</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 text-zinc-500" />
                      <span>Copiar Resposta</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
